import {
  Box,
  Button,
  ChakraProvider,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  theme,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { searchQueryFun, updateSubscriber } from "./ManageUserFun";

type ManageVipProps = {
  setUserData: React.Dispatch<React.SetStateAction<Object[]>>;
};

const ManageVip: React.FC<ManageVipProps> = () => {
  const [inputPayload, setInputPayload] = useState({
    email: "",
    sub_id: "",
    collection: "sub",
  });

  const [data, setUserData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);

  const handleInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setInputPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateInputPayload = (payload: { email: string; sub_id: string }) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const objectIdRegex = /^[a-f\d]{24}$/i;

    const trimmedEmail = payload.email.trim();
    const trimmedSubscriberId = payload.sub_id.trim();

    const isEmailValid =
      emailRegex.test(trimmedEmail) && trimmedEmail.toLowerCase().endsWith(".com");
    const isSubscriberIdValid = objectIdRegex.test(trimmedSubscriberId);

    return isEmailValid || isSubscriberIdValid;
  };

  const handleSearch = async () => {
    if (
      !validateInputPayload({
        email: inputPayload.email,
        sub_id: inputPayload.sub_id,
      })
    ) {
      alert("Enter either a valid email or a valid subscriber ID to search");
      return;
    }

    setLoading(true);
    setSearchAttempted(true);

    try {
      const response = await searchQueryFun(inputPayload);
      setUserData(response?.subResponse || []);
    } catch (error) {
      alert("An error occurred while searching.");
    } finally {
      setLoading(false);
    }
  };

  const checkTheRequest = (type: "grant" | "revoked") => {
    const isGranting = type === "grant";
    const allMatch = data.every((elem) => !!elem.isSubscriberVip === isGranting);

    if (allMatch) {
      if (isGranting) {
        alert("All these subscribers already have VIP status.");
      } else {
        alert("None of these subscribers currently have VIP status.");
      }
      return false;
    }

    return true;
  };

  const handleVipStatus = async (type: "grant" | "revoked") => {
    if (!checkTheRequest(type)) return;

    try {
      const isGranting = type === "grant";
      const updatePayload = { isSubscriberVip: isGranting };
      const subscribersToUpdate = data.filter(
        (elem) => !!elem.isSubscriberVip !== isGranting
      );
      const updatePromises = subscribersToUpdate.map((elem) =>
        updateSubscriber(elem._id, updatePayload)
      );

      const responses = await Promise.all(updatePromises);
      const allSuccessful = responses.every((res) => res);

      if (allSuccessful) {
        alert(
          isGranting
            ? "VIP status granted successfully."
            : "VIP status revoked successfully."
        );
      } else {
        alert("Something went wrong with some updates. Please try again.");
      }
      await handleSearch();
    } catch (error) {
      alert("An error occurred while updating VIP status.");
    }
  };

  return (
    <ChakraProvider theme={theme}>
      <Box pb={100}>
        <Box p={6}>
          <Heading size="md" mb={4}>
            Manage VIP Customer:
          </Heading>

          <Box display="flex" gap={6} alignItems="flex-end" flexWrap="wrap">
            <Checkbox isChecked={true} isReadOnly mb={1}>
              Search by Subscriber
            </Checkbox>

            <FormControl w="250px">
              <FormLabel>Subscriber Email</FormLabel>
              <Input
                placeholder="Enter Subscriber Email"
                name="email"
                value={inputPayload.email}
                onChange={handleInput}
              />
            </FormControl>

            <FormControl w="250px">
              <FormLabel>Subscriber ID</FormLabel>
              <Input
                placeholder="Enter Subscriber ID"
                name="sub_id"
                value={inputPayload.sub_id}
                onChange={handleInput}
              />
            </FormControl>

            <Button colorScheme="green" size="md" onClick={handleSearch}>
              Search
            </Button>

            <Button
              colorScheme="green"
              size="md"
              onClick={() => handleVipStatus("grant")}
              isDisabled={data.length === 0}
              sx={{
                _disabled: {
                  bg: "gray.300",
                  color: "gray.600",
                  cursor: "not-allowed",
                  opacity: 1,
                  boxShadow: "base",
                },
              }}
            >
              Grant VIP Status
            </Button>

            <Button
              colorScheme="green"
              size="md"
              onClick={() => handleVipStatus("revoked")}
              isDisabled={data.length === 0}
              sx={{
                _disabled: {
                  bg: "gray.300",
                  color: "gray.600",
                  cursor: "not-allowed",
                  opacity: 1,
                  boxShadow: "base",
                },
              }}
            >
              Revoke VIP Status
            </Button>
          </Box>
        </Box>

        <Box>
          {loading ? (
            <Spinner
              ml="48%"
              mt="150px"
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="green.500"
              size="xl"
            />
          ) : data.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Subscriber ID</Th>
                  <Th>VIP Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((elem: any) => (
                  <Tr key={elem._id}>
                    <Td>{elem.name}</Td>
                    <Td>{elem.email}</Td>
                    <Td>{elem._id}</Td>
                    <Td>{elem.isSubscriberVip ? "Yes" : "No"}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : searchAttempted ? (
            <Box textAlign="center" mt={10} color="gray.600" fontSize="lg">
              No subscriber found.
            </Box>
          ) : null}
        </Box>
      </Box>
    </ChakraProvider>
  );
};

export default ManageVip;
